import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => n.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.2em] text-choco/75">{label}</span>
    <input
      {...props}
      className="mt-2 w-full bg-transparent border-b border-choco/25 focus:border-gold outline-none py-2 text-choco font-light transition-colors duration-300"
    />
  </label>
);

export default function Checkout({ open, onClose }) {
  const { items, total, discount, grandTotal, promo, clear, setOpen } = useCart();
  const [form, setForm] = useState({ prenom: "", nom: "", email: "", adresse: "", code_postal: "", ville: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);
  const [shipping, setShipping] = useState("standard");
  const shippingCost = shipping === "vip" ? 4.9 : 0;
  const displayedTotal = Math.round((grandTotal + shippingCost) * 100) / 100;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/orders`, {
        customer: form,
        items: items.map((i) => ({ slug: i.slug, qty: i.qty, size: i.size, color: i.color })),
        promo_code: promo ? promo.code : null,
        shipping_method: shipping,
      });
      setOrder(data);
      clear();
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue, veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    onClose();
    if (order) setTimeout(() => setOrder(null), 400);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="checkout-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-choco-deep/60 flex items-center justify-center p-4"
          onClick={close}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-ivory w-full max-w-lg max-h-[90vh] overflow-y-auto p-8 md:p-12 relative"
          >
            <button data-testid="checkout-close-button" onClick={close} aria-label="Fermer" className="absolute top-5 right-5 text-choco/65 hover:text-gold transition-colors">
              <X size={20} strokeWidth={1.5} />
            </button>

            {!order ? (
              <>
                <h2 className="font-serif text-3xl font-light text-choco">Votre commande</h2>
                <p className="mt-2 text-sm text-choco/75 font-light">
                  {promo && <>Code <span className="text-gold">{promo.code}</span> (-{promo.percent} %) — </>}Total : <span className="font-serif text-choco" data-testid="checkout-total">{eur(displayedTotal)}</span>
                </p>
                <div className="mt-6 space-y-3" data-testid="shipping-options">
                  <p className="text-xs uppercase tracking-[0.2em] text-choco/75">Mode de livraison</p>
                  <label className={`flex items-start gap-3 border px-4 py-3 cursor-pointer transition-colors duration-300 ${shipping === "standard" ? "border-gold bg-gold/5" : "border-choco/20 hover:border-gold"}`}>
                    <input type="radio" name="shipping" data-testid="shipping-standard" checked={shipping === "standard"} onChange={() => setShipping("standard")} className="mt-1 accent-[#C9A15C]" />
                    <span className="text-sm font-light text-choco">
                      Livraison Standard — <span className="text-gold">gratuite</span>
                      <span className="block text-xs text-choco/75 mt-0.5">France métropolitaine · 7 à 15 jours ouvrés</span>
                    </span>
                  </label>
                  <label className={`flex items-start gap-3 border px-4 py-3 cursor-pointer transition-colors duration-300 ${shipping === "vip" ? "border-gold bg-gold/5" : "border-choco/20 hover:border-gold"}`}>
                    <input type="radio" name="shipping" data-testid="shipping-vip" checked={shipping === "vip"} onChange={() => setShipping("vip")} className="mt-1 accent-[#C9A15C]" />
                    <span className="text-sm font-light text-choco">
                      Livraison VIP — 4,90 €
                      <span className="block text-xs text-choco/75 mt-0.5">✅ Inclut une assurance perte, casse ou vol</span>
                    </span>
                  </label>
                </div>
                <form data-testid="checkout-form" onSubmit={submit} className="mt-8 space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <Field label="Prénom" required value={form.prenom} onChange={set("prenom")} data-testid="checkout-prenom" />
                    <Field label="Nom" required value={form.nom} onChange={set("nom")} data-testid="checkout-nom" />
                  </div>
                  <Field label="E-mail" type="email" required value={form.email} onChange={set("email")} data-testid="checkout-email" />
                  <Field label="Adresse" required value={form.adresse} onChange={set("adresse")} data-testid="checkout-adresse" />
                  <div className="grid grid-cols-2 gap-6">
                    <Field label="Code postal" required value={form.code_postal} onChange={set("code_postal")} data-testid="checkout-codepostal" />
                    <Field label="Ville" required value={form.ville} onChange={set("ville")} data-testid="checkout-ville" />
                  </div>
                  <p data-testid="payment-notice" className="text-xs text-denim bg-denim/10 px-4 py-3 leading-relaxed">
                    Le paiement en ligne n'est pas encore activé : votre commande est enregistrée
                    sans règlement et Karine vous contactera personnellement pour la finaliser.
                    Bientôt, choisissez votre mode de paiement — carte bancaire ou Klarna en plusieurs fois.
                  </p>
                  {error && <p data-testid="checkout-error" className="text-sm text-red-700">{error}</p>}
                  <button
                    data-testid="checkout-submit-button"
                    type="submit"
                    disabled={loading || items.length === 0}
                    className="w-full bg-choco text-ivory text-xs uppercase tracking-[0.25em] py-4 hover:bg-denim transition-colors duration-400 disabled:opacity-50"
                  >
                    {loading ? "Envoi en cours…" : "Confirmer la commande"}
                  </button>
                </form>
              </>
            ) : (
              <div data-testid="order-success" className="text-center py-8">
                <p className="text-gold text-3xl">✦</p>
                <h2 className="font-serif text-3xl font-light text-choco mt-4">Merci, {order.customer.prenom}</h2>
                <p className="mt-4 text-choco/85 font-light leading-relaxed">
                  Votre commande <span className="font-serif text-choco" data-testid="order-number">{order.number}</span> est
                  bien enregistrée. Karine vous contactera personnellement à
                  l'adresse {order.customer.email} pour la finaliser avec vous.
                </p>
                <div className="mt-8 border-t hairline-gold pt-6 space-y-2 text-sm text-left max-w-xs mx-auto">
                  {order.items.map((i) => (
                    <div key={i.slug} className="flex justify-between">
                      <span>{i.qty} × {i.name}{i.size ? ` (${i.size})` : ""}{i.color ? ` — ${i.color}` : ""}</span>
                      <span className="font-serif">{eur(i.price * i.qty)}</span>
                    </div>
                  ))}
                  {order.discount > 0 && (
                    <div className="flex justify-between text-gold">
                      <span>Remise {order.promo_code}</span>
                      <span>-{eur(order.discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-serif text-lg pt-3 border-t hairline-gold">
                    <span>Total</span>
                    <span>{eur(order.total)}</span>
                  </div>
                </div>
                <button
                  data-testid="order-continue-button"
                  onClick={close}
                  className="mt-10 border border-choco text-choco text-xs uppercase tracking-[0.25em] px-10 py-3 hover:bg-choco hover:text-ivory transition-colors duration-400"
                >
                  Continuer ma visite
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
